import { uploadedMediaUrl, imageAvatarUrl } from 'lib/utils/imageUtils';
import Link from 'next/link';
import { Avatar } from '@mui/material';
import { Concept } from 'lib/types/projects';
import { ConceptStatus } from 'components/ConceptStatus';
import { Delete } from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useModal } from 'mui-modal-provider';
import { DuplicateConceptModal } from 'components/ConceptForm/DuplicateConceptModal';
import { useApplicationState } from 'lib/contexts/ApplicationState';
import { MobileTooltip } from 'components/MobileTooltip';
import { toSlug } from 'lib/utils/filterUtils';

interface Props {
  concept: Concept;
  onDelete?: () => void;
  onEdit?: (concept: Concept) => void;
  deleteText?: string;
  hideButtons?: boolean;
}

export const ConceptCard = ({
  concept, onDelete, onEdit, deleteText, hideButtons = false,
}: Props) => {
  const { project } = concept;
  const client = concept.project?.clients?.[0];
  const { enqueueSnackbar } = useSnackbar();
  const { showModal } = useModal();
  const { api } = useApplicationState();
  const [buttonVisible, setButtonsVisible] = useState(false);

  const projectLink = project && (
    <div>
      <span className="mr-2">Project:</span>
      <Link href={`/projects/${toSlug(project?.publicId, project?.name)}`} className="hover:underline">
        {project?.name}
      </Link>
    </div>
  );

  const handleCopyText = async (text?: string | null) => {
    if (!text) return;
    if (navigator.share) {
      try {
        await navigator.share({
          text,
        });
      } catch (err) {
        // noop
      }
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      enqueueSnackbar('Text copied!', { variant: 'success' });
    }
  };

  const onDuplicate = (c: Concept) => {
    if (!c.teamId) return;

    const modal = showModal(DuplicateConceptModal, {
      concept: c,
      teamId: c.teamId,
      onCancel: () => {
        modal.destroy();
      },
      onDuplicate: async (prj: { projectId?: number; name?: string }) => {
        if (!concept.id) {
          return;
        }
        const dupeConcept = await api.duplicateConcept(concept.id, prj.projectId, prj.name);
        const action = () => (
          <button type="button" className="btn">
            <a href={`/teams/${dupeConcept.teamId}/brainstorm/${dupeConcept.id}`}>
              View concept
            </a>
          </button>
        );
        enqueueSnackbar('Concept duplicated!', { variant: 'success', action });
        modal.destroy();
      },
    });
  };

  return (
    <li
      className="relative w-full h-full p-8 border rounded flex flex-col justify-between"
      onMouseEnter={() => !hideButtons && setButtonsVisible(true)}
      onMouseLeave={() => setButtonsVisible(false)}
    >
      <div className="absolute top-0 right-0"><ConceptStatus concept={concept} /></div>
      <div>
        {client ? (
          <div className="flex items-center gap-2 mb-2">
            <div>
              <Avatar
                sx={{ width: 50, height: 50, margin: 'auto' }}
                variant="circular"
                alt={client.name}
                src={imageAvatarUrl(client.avatarUrl, 100)}
              />
            </div>
            <div>
              <div>
                <span className="mr-4">Client:</span>
                {client.name}
              </div>
              {projectLink}
            </div>
          </div>
        ) : projectLink}
        <div className="flex">
          <div className="font-bold text-xl pt-4">
            <div className={`${buttonVisible ? 'opacity-100' : 'opacity-0'} absolute left-2`}>
              <MobileTooltip
                title="Copy title"
                key="add"
                arrow
                placement="right"
              >
                <button type="button">
                  <ContentCopyIcon fontSize="small" onClick={() => handleCopyText(concept.title)} />
                </button>
              </MobileTooltip>
            </div>
            <Link href={`/teams/${concept.teamId}/brainstorm/${concept.id}`} className="hover:underline">
              {concept.title}
            </Link>
          </div>
        </div>
        <div>
          <span>
            {concept.description && (
              <div className={`${buttonVisible ? 'opacity-100' : 'opacity-0'} absolute left-2`}>
                <MobileTooltip
                  title="Copy description"
                  key="add"
                  arrow
                  placement="right"
                >
                  <button type="button">
                    <ContentCopyIcon fontSize="small" onClick={() => handleCopyText(concept.description)} />
                  </button>
                </MobileTooltip>
              </div>
            )}
            <div className="mb-2 overflow-hidden h-24">
              {concept.description}
            </div>
          </span>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-2 items-center">
          {concept.media?.slice(0, 4).map((m) => (
            <div key={m.id}>
              <img
                src={uploadedMediaUrl(m.imageUrl, 320)}
                alt={m.imageUrl}
                className="max-w-80"
              />
            </div>
          ))}
        </div>
      </div>
      <span className={`space-x-2 ${buttonVisible ? '' : 'h-7'}`}>
        {buttonVisible && (
          <>
            {onDelete && (
              <button
                type="button"
                className={`${deleteText ? 'w-1/4 btn-primary-xs' : ''}`}
                onClick={onDelete}
              >
                {deleteText || <Delete fontSize="small" />}
              </button>
            )}
            {onEdit && (
              <button
                type="button"
                className="btn-secondary-xs w-1/4"
                onClick={() => onEdit(concept)}
              >
                Edit
              </button>
            )}
            <button
              type="button"
              className="btn-secondary-xs w-1/3"
              onClick={() => onDuplicate(concept)}
            >
              Duplicate
            </button>
          </>
        )}
      </span>
    </li>
  );
};
